
import { clamp01 } from "../clamp";


const FADE_MS = 1500;
const FADE_STEP_MS = 50;

/** How close to the end a track starts folding back into itself. */
const LOOP_LEAD_MS = FADE_MS + 250;

export type TrackName =
  | "login_window"
  | "characterselect"
  | "wedding"
  | "m2bg"
  | "enter_the_east"
  // The other ten `settings.lua:121-170` field tracks a hosted map actually
  // uses (`server/field_music.py` owns which map gets which); shipped by
  // `tools/build_audio.sh` from the full 2014 tree's `client/bin/BGM/`.
  | "back_to_back"
  | "open_the_gate"
  | "a_rhapsody_of_war"
  | "wonderland"
  | "mountain_of_death"
  | "save_me"
  | "follow_war_god"
  | "death_of_landmark"
  | "Only_my_battle"
  | "last-war2"
  | "xmas"
  // The last seven distinct field tracks (`settings.lua:131,143-145,164,167-
  // 170`), for the nine hosted maps that fell through to the default `m2bg`
  // track until now: trent02; the three `monkey_dungeon` maps; devilcatacomb;
  // CapeDragonHead, dawnmistwood, BayBlackSand and Mt_Thunder.
  | "another_way"
  | "blacksea"
  | "catacomb_of_devil"
  | "lost_my_name"
  | "misty_forest"
  | "monkey_temple"
  | "mt";

export interface MusicOptions {
  /** Where the audio lives. Trailing slash optional. */
  readonly base?: string;
  /** 0..1. Applied on top of each fade. */
  readonly volume?: number;
  /** Injected in tests; defaults to the real element factory. */
  readonly createAudio?: () => HTMLAudioElement;
}

interface Layer {
  element: HTMLAudioElement;
  fade: ReturnType<typeof setInterval> | null;
  started: boolean;
}

const SOURCES: ReadonlyArray<readonly [string, string]> = [
  [".opus", 'audio/ogg; codecs="opus"'],
  [".m4a", 'audio/mp4; codecs="mp4a.40.2"'],
];

export class Music {
  private readonly base: string;
  private readonly create: () => HTMLAudioElement;
  private readonly layers: [Layer, Layer];
  private active = 0;

  private current: TrackName | null = null;
  private armed: TrackName | null = null;
  /** Invalidates an autoplay attempt that a later gesture or track replaced. */
  private startSerial = 0;
  /** The field theme a player selected in System Options, if any. */
  private preferred: TrackName | null = null;

  /** True when the browser refused playback and no gesture has arrived yet. */
  blocked = false;
  private volumeValue: number;
  /** Map/event multiplier selected by the server, distinct from local options. */
  private fieldVolumeValue = 1;
  private mutedValue = false;

  constructor(options: MusicOptions = {}) {
    this.base = (options.base ?? "/audio").replace(/\/$/, "");
    this.volumeValue = clamp01(options.volume ?? 0.4);
    this.create =
      options.createAudio ?? (() => document.createElement("audio"));

    this.layers = [this.makeLayer(), this.makeLayer()];
  }

  private makeLayer(): Layer {
    const element = this.create();
    element.preload = "none";
    element.volume = 0;
    return { element, fade: null, started: false };
  }

  // --------------------------------------------------------------- controls

  play(track: TrackName): void {
    if (track === this.current && !this.blocked) return;
    this.armed = track;
    void this.start(track);
  }

  /** Remember and immediately try a player-selected field theme. */
  select(track: TrackName): void {
    this.preferred = track;
    this.play(track);
  }

  get preferredTrack(): TrackName | null {
    return this.preferred;
  }

  /** Release music that autoplay policy deferred. Call from a real gesture. */
  unlock(): void {
    // Safari may leave play() pending while it waits for source metadata or
    // autoplay policy instead of rejecting immediately. The gesture is still
    // the recovery opportunity, even when `blocked` has not flipped yet.
    if (this.armed === null) return;
    if (this.current === this.armed) {
      // A background/interruption can pause the active layer after autoplay was
      // already unlocked. It is still the same track, so resume that element
      // in place rather than starting a second layer and cross-fading from it.
      const active = this.layers[this.active];
      if (active.started && active.element.paused) this.resumeCurrent(active, this.armed);
      return;
    }
    this.blocked = false;
    void this.start(this.armed);
  }

  stop(): void {
    this.startSerial += 1;
    this.armed = null;
    this.current = null;
    for (const layer of this.layers) {
      this.cancelFade(layer);
      layer.element.pause();
      layer.element.volume = 0;
      layer.started = false;
    }
  }

  get muted(): boolean {
    return this.mutedValue;
  }

  set muted(value: boolean) {
    this.mutedValue = value;
    for (const layer of this.layers) layer.element.muted = value;
  }

  get volume(): number {
    return this.volumeValue;
  }

  set volume(value: number) {
    this.volumeValue = clamp01(value);
    // Only the active layer is raised; a layer mid-fade keeps its own ramp so a
    // volume change during a cross-fade does not snap the outgoing track back.
    const layer = this.layers[this.active];
    if (layer.fade === null) layer.element.volume = this.effectiveVolume;
  }

  /** The server's map/event factor. It never comes from a browser control. */
  get fieldVolume(): number {
    return this.fieldVolumeValue;
  }

  set fieldVolume(value: number) {
    this.fieldVolumeValue = clamp01(value);
    const layer = this.layers[this.active];
    if (layer.fade === null) layer.element.volume = this.effectiveVolume;
  }

  /** Cross-fade to a track selected by the shard, preserving the local slider. */
  playField(track: TrackName, volume: number): void {
    this.fieldVolume = volume;
    this.play(track);
  }

  get playing(): TrackName | null {
    return this.current;
  }

  // ---------------------------------------------------------------- internals

  private async start(track: TrackName): Promise<void> {
    const serial = ++this.startSerial;
    const next = this.layers[1 - this.active];
    const previous = this.layers[this.active];

    this.source(next.element, track);
    next.element.volume = 0;
    next.element.muted = this.mutedValue;
    next.element.currentTime = 0;

    try {
      await next.element.play();
    } catch {
      // Autoplay refused. Keep `armed` so the next gesture can release it, and
      // say so rather than failing silently.
      if (serial === this.startSerial && this.armed === track) this.blocked = true;
      return;
    }

    // A later gesture or track replaced this attempt while play() was pending.
    // Do not let the stale promise steal `current` or the active layer back.
    if (serial !== this.startSerial || this.armed !== track) return;

    this.blocked = false;
    this.current = track;
    this.active = 1 - this.active;
    next.started = true;

    this.fadeTo(next, this.effectiveVolume);
    if (previous.started) {
      this.fadeTo(previous, 0, () => {
        previous.element.pause();
        previous.started = false;
      });
    }

    this.scheduleLoop(next, track);
  }

  /** Resume the already-audible layer after Safari paused it in the background. */
  private resumeCurrent(layer: Layer, track: TrackName): void {
    const serial = ++this.startSerial;
    try {
      void layer.element.play().then(
        () => {
          if (serial === this.startSerial && this.current === track && this.armed === track) {
            this.blocked = false;
          }
        },
        () => {
          if (serial === this.startSerial && this.current === track && this.armed === track) {
            this.blocked = true;
          }
        },
      );
    } catch {
      if (serial === this.startSerial && this.current === track && this.armed === track) {
        this.blocked = true;
      }
    }
  }

  private source(element: HTMLAudioElement, track: TrackName): void {
    // Rebuilt rather than mutated: assigning `src` directly would skip the
    // format negotiation and hand Safari an Opus file it cannot decode.
    while (element.firstChild) element.removeChild(element.firstChild);
    for (const [extension, type] of SOURCES) {
      const source = document.createElement("source");
      source.src = `${this.base}/${track}${extension}`;
      source.type = type;
      element.appendChild(source);
    }
    element.load();
  }

  private scheduleLoop(layer: Layer, track: TrackName): void {
    const onTime = () => {
      const { duration, currentTime } = layer.element;
      if (!Number.isFinite(duration) || duration <= 0) return;
      if ((duration - currentTime) * 1000 > LOOP_LEAD_MS) return;

      layer.element.removeEventListener("timeupdate", onTime);
      if (this.current === track) void this.start(track);
    };
    layer.element.addEventListener("timeupdate", onTime);
  }

  private fadeTo(layer: Layer, target: number, done?: () => void): void {
    this.cancelFade(layer);

    const from = layer.element.volume;
    const startedAt = Date.now();

    layer.fade = setInterval(() => {
      const t = FADE_MS <= 0 ? 1 : Math.min(1, (Date.now() - startedAt) / FADE_MS);
      layer.element.volume = clamp01(from + (target - from) * t);
      if (t >= 1) {
        this.cancelFade(layer);
        done?.();
      }
    }, FADE_STEP_MS);
  }

  private cancelFade(layer: Layer): void {
    if (layer.fade !== null) {
      clearInterval(layer.fade);
      layer.fade = null;
    }
  }

  private get effectiveVolume(): number {
    return this.volumeValue * this.fieldVolumeValue;
  }
}
